import { useQuery } from '@tanstack/react-query';
import { useState, type ReactElement } from 'react';
import { sectorSetupListQuery } from '../api/settings';
import { SettingHistoryDrawer } from '../settings/history';
import { fmvNumber, fmvText, formatPerShare, leadChange } from '../settings/model';
import { ChangedLine, useSetting } from '../settings/parts';
import { Card, formatDate, formatCount, plural } from '../ui';
import { ledSectors, type ExecutiveModel } from './model';
import { SetupSteps, type SetupStepState } from './SetupSteps';

const BUDGETS_HREF = '/manage/executive/budget-pools';
const VALUES_HREF = '/manage/executive/values';
const FMV = 'values.fmvCents';
const SECTOR_LEADS = 'directory.sectorLeads';

/**
 * FR3 · what only the Executives own before go-live, built from the real settings: every pool's
 * budget, the FMV, and the Sector Leads. Done items fold away, and the card
 * goes once every required item is done (DESIGN-SPEC §5 "Owners' setup"). A PAA's options share
 * is the Stock Admin team's since APP-151, so it isn't asked for here.
 */
export function ExecutiveSetupCard({ ex }: { ex: ExecutiveModel }): ReactElement | null {
  const fy = String(ex.fiscalYear);
  const fmv = useSetting(FMV);
  // The Sector Leads list's last change (kept in Settings › System administration › Roles & lists), for its done row.
  const leadsChanged = useSetting(SECTOR_LEADS);
  const [history, setHistory] = useState<'fmv' | 'leads' | null>(null);
  const sectors = useQuery(sectorSetupListQuery);

  const total = ex.pools.length;
  const budgetsDone = total > 0 && ex.poolsSet === total;
  const fmvDone = fmv.status === 'ready' && fmv.view.lastChanged !== null;
  const leads = ledSectors(sectors.data?.sectors ?? [], ex.pools);
  const withoutLead = leads.filter((s) => s.lead === null);
  const leadsDone = sectors.isSuccess && withoutLead.length === 0;

  const steps: SetupStepState[] = [];
  // No pools to show (Preview): nothing to say about budgets either.
  if (total > 0)
    steps.push({
      id: 'budgets',
      done: budgetsDone,
      title: `Budgets for ${plural(total, 'pool', 'pools')}`,
      status: `${formatCount(ex.poolsSet)} of ${formatCount(total)}`,
      body: budgetsDone
        ? `Every ${fy} pool has a budget.`
        : `Set each pool’s cash and options for ${fy}. A pool starts counting as soon as it has a budget, and awards can’t be checked against one that has none.`,
      action: budgetsDone
        ? null
        : { label: ex.poolsSet === 0 ? 'Set budgets' : 'Set budget', href: BUDGETS_HREF },
    });
  if (fmv.status === 'ready') {
    const value = typeof fmv.view.value === 'number' ? formatPerShare(fmv.view.value) : null;
    steps.push({
      id: 'fmv',
      done: fmvDone,
      title: 'FMV',
      status: fmvDone ? null : 'To do',
      summary:
        value === null
          ? null
          : `${value} per share${fmv.view.effectiveFrom ? ` from ${formatDate(fmv.view.effectiveFrom)}` : ''}`,
      body: 'The fair market value every award’s options are worked out from.',
      // FR3: who last changed it, with its History, on the done row too.
      changed: (
        <ChangedLine
          change={fmv.view.lastChanged}
          onHistory={() => {
            setHistory('fmv');
          }}
        />
      ),
      action: fmvDone ? null : { label: 'Set the FMV', href: VALUES_HREF },
    });
  }
  if (sectors.isSuccess && leads.length > 0) {
    steps.push({
      id: 'leads',
      done: leadsDone,
      title: 'Sector Leads list',
      status: leadsDone ? null : `${formatCount(withoutLead.length)} without a lead`,
      summary: `read-only, set by a System Admin · ${plural(leads.length - withoutLead.length, 'Sector Lead', 'Sector Leads')}`,
      body: leadsDone
        ? 'Every sector has a lead.'
        : `${withoutLead.map((s) => s.name).join(', ')} ${withoutLead.length === 1 ? 'has' : 'have'} no Sector Lead yet. A System Admin sets them in Settings › System administration › Roles & lists.`,
      ...(leadsChanged.status === 'ready' && {
        changed: (
          <ChangedLine
            change={leadsChanged.view.lastChanged}
            onHistory={() => {
              setHistory('leads');
            }}
          />
        ),
      }),
      action: null,
    });
  }

  const required = steps.filter((step) => step.optional !== true);
  const doneCount = required.filter((step) => step.done).length;
  if (required.length > 0 && doneCount === required.length) return null;

  return (
    <Card
      title={`Setup for ${fy}`}
      accent="cyan"
      help={{
        shows: `What only the Executives set before go-live, from the real settings: the ${fy} pools, the FMV, requesters and the Sector Leads.`,
        canDo:
          'Open each item to set it. Done items fold away, and this list goes once everything is set.',
      }}
    >
      <SetupSteps
        label={`Setup for ${fy}`}
        steps={steps}
        doneCount={doneCount}
        requiredCount={required.length}
        footer={`Done items fold away. This list disappears once everything is set. Bringing in ${fy} history from the old tracker is a System Admin’s import; you’ll see it in the audit log.`}
      />
      <SettingHistoryDrawer
        isOpen={history === 'fmv'}
        onOpenChange={(open) => {
          if (!open) setHistory(null);
        }}
        settingKey={FMV}
        title="FMV per share"
        subject="History · Areté stock"
        format={fmvText}
        numeric={fmvNumber}
        formatNumber={formatPerShare}
      />
      <SettingHistoryDrawer
        isOpen={history === 'leads'}
        onOpenChange={(open) => {
          if (!open) setHistory(null);
        }}
        settingKey={SECTOR_LEADS}
        title="Sector Leads"
        subject="History · Roles & lists"
        format={leadChange}
        listChanges
      />
    </Card>
  );
}
